/**
 * On-demand BPM / offset detection for the Synth VR Controls panel (mirrors the
 * Flow editor's Auto BPM / Auto Offset). BPM runs the backend librosa analysis;
 * Offset scans the decoded audio for the first audible onset (client-side).
 */

import { useEditorStore } from '../../store/editorStore';
import { getAudioUrl } from '../../api/client';
import { API_BASE } from '../../api/apiBase';

async function fetchAudioBytes(): Promise<{ bytes: ArrayBuffer; type: string }> {
  const beatmap = useEditorStore.getState().beatmap;
  if (!beatmap) throw new Error('No beatmap loaded');
  const res = await fetch(getAudioUrl(beatmap.id));
  if (!res.ok) throw new Error('Could not read the audio');
  const bytes = await res.arrayBuffer();
  const type = res.headers.get('content-type') || 'audio/mpeg';
  return { bytes, type };
}

/** Re-run the backend BPM analysis on the loaded track. */
export async function detectSrBpm(): Promise<number> {
  const { bytes, type } = await fetchAudioBytes();
  const form = new FormData();
  form.append('audio', new Blob([bytes], { type }), 'audio');
  const res = await fetch(`${API_BASE}/api/audio/analyze`, { method: 'POST', body: form });
  if (!res.ok) throw new Error(`Analysis failed (${res.status})`);
  const j = await res.json();
  const bpm = Number(j?.bpm);
  if (!Number.isFinite(bpm) || bpm <= 0) throw new Error('No BPM detected');
  return Math.round(bpm * 100) / 100;
}

/** First audible onset (seconds) — skips leading silence / hiss. Client-side. */
export async function detectSrOffset(): Promise<number> {
  const { bytes } = await fetchAudioBytes();
  const Ctx: typeof AudioContext =
    window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  const ctx = new Ctx();
  let buffer: AudioBuffer;
  try { buffer = await ctx.decodeAudioData(bytes.slice(0)); }
  finally { ctx.close().catch(() => { /* ignore */ }); }

  const data = buffer.getChannelData(0);
  const sr = buffer.sampleRate;
  const win = Math.max(1, Math.floor(sr * 0.010)); // 10 ms windows
  const nWin = Math.floor(data.length / win);
  if (nWin === 0) return 0;

  const rms = new Float32Array(nWin);
  let peak = 0;
  for (let w = 0; w < nWin; w++) {
    const base = w * win;
    let sum = 0;
    for (let i = 0; i < win; i++) { const s = data[base + i]; sum += s * s; }
    const r = Math.sqrt(sum / win);
    rms[w] = r;
    if (r > peak) peak = r;
  }
  if (peak <= 0) return 0;

  const threshold = Math.max(peak * 0.08, 0.008);
  const sustain = 3;
  for (let w = 0; w < nWin; w++) {
    if (rms[w] < threshold) continue;
    let ok = true;
    for (let k = 1; k < sustain && w + k < nWin; k++) {
      if (rms[w + k] < threshold * 0.6) { ok = false; break; }
    }
    if (!ok) continue;
    const t = (w * win) / sr - 0.005;
    return Math.max(0, Math.round(t * 1000) / 1000);
  }
  return 0;
}
